import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuthState, pushActivity, setAuthFromToken, setRoute, setSessionChecked, setStoreMallUuid, setStoreName, setStoreToken, setStoreUuid } from '../../app/providers/store';
import createRequest, { setGlobalRequestClientConfig } from '../api/portalApiClient';
import { resolveApiBaseForPath } from '../api/requestRouting';
import { parseJwt, resolveRole } from '../utils/jwt';
import { AUTH_REFRESH, AUTH_SESSION_ME, AUTH_STORES_LOGOUT } from '../constants/endpoints';
import { logger } from '../utils/logger.js';
import { storage } from '../utils/storage.js';

const LOG_MODULE = 'useAppController';
const PROACTIVE_REFRESH_BUFFER_MS = 60 * 1000;

const useAppController = (apiBase) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const storeToken = useSelector((state) => state.store.storeToken);
  const storeUuid = useSelector((state) => state.store.storeUuid);
  const route = useSelector((state) => state.route.path);
  const isStoreLoggedIn = auth.role === 'store' && Boolean(storeToken || storeUuid);
  const [sessionExpiredAt, setSessionExpiredAt] = useState(0);
  const [sessionBootstrapComplete, setSessionBootstrapComplete] = useState(false);
  const refreshInFlightRef = useRef(null);
  const proactiveRefreshTimerRef = useRef(null);

  const clearSessionState = useCallback(({ expired = false } = {}) => {
    storage.purgeLegacyWebAuthStorage();
    dispatch(clearAuthState());
    dispatch(setStoreToken(''));
    dispatch(setStoreUuid(''));
    dispatch(setStoreMallUuid(''));
    dispatch(setStoreName(''));
    if (expired) {
      setSessionExpiredAt(Date.now());
    } else {
      setSessionExpiredAt(0);
    }
  }, [dispatch]);

  const refreshAccessToken = useCallback(async (_expiredToken = '', roleOverride = '') => {
    const tokenRole = roleOverride || auth.role || 'user';

    logger.debug(LOG_MODULE, 'Refresh token flow started', {
      source: { file: 'shared/hooks/useAppController.js', method: 'refreshAccessToken' },
      tokenRole
    });

    if (refreshInFlightRef.current) {
      logger.debug(LOG_MODULE, 'Refresh token request already in flight', {
        source: { file: 'shared/hooks/useAppController.js', method: 'refreshAccessToken' },
        tokenRole
      });
      return refreshInFlightRef.current;
    }

    refreshInFlightRef.current = (async () => {
      try {
        const refreshPath = AUTH_REFRESH(tokenRole);
        const requestInit = {
          method: 'POST',
          credentials: 'include'
        };

        const response = await fetch(`${resolveApiBaseForPath(apiBase, refreshPath)}${refreshPath}`, requestInit);

        if (!response.ok) {
          logger.warn(LOG_MODULE, 'Refresh token request failed', {
            source: { file: 'shared/hooks/useAppController.js', method: 'refreshAccessToken' },
            tokenRole,
            status: response.status
          });
          return null;
        }
        const payload = await response.json();
        const nextAccessToken = payload?.data?.accessToken || payload?.accessToken || '';

        if (!nextAccessToken) {
          logger.warn(LOG_MODULE, 'Refresh response missing access token', {
            source: { file: 'shared/hooks/useAppController.js', method: 'refreshAccessToken' },
            tokenRole
          });
          return null;
        }

        dispatch(setAuthFromToken({ token: nextAccessToken, roleHint: tokenRole }));
        if (tokenRole === 'store') {
          dispatch(setStoreToken(nextAccessToken));
        }
        setSessionExpiredAt(0);
        logger.info(LOG_MODULE, 'Refresh token succeeded', {
          source: { file: 'shared/hooks/useAppController.js', method: 'refreshAccessToken' },
          tokenRole
        });
        return { accessToken: nextAccessToken };
      } catch (error) {
        logger.error(LOG_MODULE, 'Refresh token flow crashed', {
          source: { file: 'shared/hooks/useAppController.js', method: 'refreshAccessToken' },
          tokenRole,
          error: error instanceof Error
            ? { name: error.name, message: error.message, stack: error.stack }
            : String(error)
        });
        return null;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    return refreshInFlightRef.current;
  }, [apiBase, auth.role, dispatch]);

  const bootstrapSession = useCallback(async ({ ignoreUnauthed = false } = {}) => {
    try {
      const response = await fetch(`${resolveApiBaseForPath(apiBase, AUTH_SESSION_ME)}${AUTH_SESSION_ME}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        if (!ignoreUnauthed) {
          clearSessionState();
          dispatch(setSessionChecked(true));
        }
        return null;
      }

      const payload = await response.json();
      const session = payload?.data || {};
      const sessionRole = String(session?.role || '').toLowerCase().includes('store') ? 'store' : 'user';
      const refreshed = await refreshAccessToken('', sessionRole);

      dispatch(setAuthFromToken({
        token: refreshed?.accessToken || '',
        roleHint: sessionRole
      }));

      if (sessionRole === 'store') {
        dispatch(setStoreUuid(session?.storeUuid || ''));
        dispatch(setStoreMallUuid(session?.mallUuid || ''));
        dispatch(setStoreName(session?.storeName || ''));
        dispatch(setStoreToken(refreshed?.accessToken || ''));
      } else {
        dispatch(setStoreToken(''));
        dispatch(setStoreUuid(''));
        dispatch(setStoreMallUuid(''));
        dispatch(setStoreName(''));
      }

      dispatch(setSessionChecked(true));
      return { session, role: sessionRole, accessToken: refreshed?.accessToken || '' };
    } catch (error) {
      logger.warn(LOG_MODULE, 'Session bootstrap failed', {
        source: { file: 'shared/hooks/useAppController.js', method: 'bootstrapSession' },
        error: error instanceof Error ? error.message : String(error)
      });
      if (!ignoreUnauthed) {
        clearSessionState();
        dispatch(setSessionChecked(true));
      }
      return null;
    }
  }, [apiBase, clearSessionState, dispatch, refreshAccessToken]);

  useEffect(() => {
    let cancelled = false;

    const runBootstrap = async () => {
      try {
        await bootstrapSession();
      } finally {
        if (!cancelled) {
          setSessionBootstrapComplete(true);
        }
      }
    };

    runBootstrap();
    return () => {
      cancelled = true;
    };
  }, [bootstrapSession]);

  useEffect(() => {
    setGlobalRequestClientConfig({
      onSessionExpired: () => setSessionExpiredAt(Date.now()),
      refreshAccessToken
    });
    return () => {
      setGlobalRequestClientConfig({});
    };
  }, [refreshAccessToken]);

  useEffect(() => {
    if (proactiveRefreshTimerRef.current) {
      window.clearTimeout(proactiveRefreshTimerRef.current);
      proactiveRefreshTimerRef.current = null;
    }

    const activeToken = auth.role === 'store' ? (storeToken || auth.token) : auth.token;
    if (!activeToken) {
      return undefined;
    }

    const claims = parseJwt(activeToken);
    const expSeconds = Number(claims?.exp || 0);
    if (!Number.isFinite(expSeconds) || expSeconds <= 0) {
      return undefined;
    }

    const expiresAtMs = expSeconds * 1000;
    const delayMs = Math.max(0, expiresAtMs - Date.now() - PROACTIVE_REFRESH_BUFFER_MS);

    proactiveRefreshTimerRef.current = window.setTimeout(async () => {
      logger.info(LOG_MODULE, 'Proactive token refresh due', {
        source: { file: 'shared/hooks/useAppController.js', method: 'proactiveRefresh' },
        role: auth.role,
        expiresAtMs,
        bufferMs: PROACTIVE_REFRESH_BUFFER_MS
      });
      const refreshed = await refreshAccessToken('', auth.role || 'user');
      if (!refreshed?.accessToken) {
        logger.warn(LOG_MODULE, 'Proactive token refresh failed; expiring session', {
          source: { file: 'shared/hooks/useAppController.js', method: 'proactiveRefresh' },
          role: auth.role
        });
        setSessionExpiredAt(Date.now());
      }
    }, delayMs);

    return () => {
      if (proactiveRefreshTimerRef.current) {
        window.clearTimeout(proactiveRefreshTimerRef.current);
        proactiveRefreshTimerRef.current = null;
      }
    };
  }, [auth.role, auth.token, refreshAccessToken, storeToken]);

  const request = useMemo(() => createRequest(apiBase, {
    onSessionExpired: () => setSessionExpiredAt(Date.now()),
    refreshAccessToken
  }), [apiBase, refreshAccessToken]);

  const handlePushActivity = (label, payload) => {
    logger.debug(LOG_MODULE, 'Push activity', {
      source: { file: 'shared/hooks/useAppController.js', method: 'handlePushActivity' },
      label
    });
    dispatch(pushActivity({
      label,
      payload,
      time: new Date().toLocaleTimeString()
    }));
  };

  const navigate = (path, { replace } = {}) => {
    logger.info(LOG_MODULE, 'Navigate', {
      source: { file: 'shared/hooks/useAppController.js', method: 'navigate' },
      path,
      replace: Boolean(replace)
    });
    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    window.dispatchEvent(new PopStateEvent('popstate'));
    dispatch(setRoute(path || '/'));
  };

  const handleAuthChange = ({ token, roleHint }) => {
    setSessionExpiredAt(0);
    logger.info(LOG_MODULE, 'Auth changed', {
      source: { file: 'shared/hooks/useAppController.js', method: 'handleAuthChange' },
      roleHint
    });
    dispatch(setAuthFromToken({ token, roleHint }));
    const role = resolveRole(token, roleHint);
    const target = role === 'store' ? '/store' : '/user';
    navigate(target);
  };

  const handleRoleSwitch = () => {
    const nextRole = auth.role === 'store' ? 'user' : 'store';
    logger.info(LOG_MODULE, 'Role switched', {
      source: { file: 'shared/hooks/useAppController.js', method: 'handleRoleSwitch' },
      nextRole
    });
    dispatch(setAuthFromToken({ token: auth.token || '', roleHint: nextRole }));
    navigate(`/${nextRole}`);
  };

   const handleStoreLogout = async () => {
     logger.info(LOG_MODULE, 'Store logout', {
       source: { file: 'shared/hooks/useAppController.js', method: 'handleStoreLogout' }
     });
     try {
       await fetch(`${resolveApiBaseForPath(apiBase, AUTH_STORES_LOGOUT)}${AUTH_STORES_LOGOUT}`, {
         method: 'POST',
         credentials: 'include'
       });
     } catch (error) {
       logger.warn(LOG_MODULE, 'Store logout request failed', {
         source: { file: 'shared/hooks/useAppController.js', method: 'handleStoreLogout' },
         error: error instanceof Error ? error.message : String(error)
       });
     }
     clearSessionState();
     setSessionExpiredAt(0);
     navigate('/', { replace: true });
   };

  return {
    auth,
    route,
    isStoreLoggedIn,
    request,
    bootstrapSession,
    sessionBootstrapComplete,
    sessionExpiredAt,
    handleAuthChange,
    handleRoleSwitch,
    handleStoreLogout,
    handlePushActivity
  };
};

export default useAppController;
